import { KernelEncodingError, base64UrlDecode, base64UrlEncode, kernelCanonicalJson, sha256DigestOf, sha256DigestSchema } from "./kernel-encoding.js";
import { LOWER_UUID_PATTERN } from "./kernel-internal.js";
import { kernelRightSchema } from "./kernel-rights.js";
import { REFUSED_DISPLAY_CODE_POINT, base64Url32BytesSchema, base64UrlTextSchema, displayTextSchema, isStepUpUrlOf, isoTimestampSchema, lowerHostSchema, stepUpUrlSchema } from "./kernel-wire-internal.js";
import { z } from "zod";
import { sha256 } from "@noble/hashes/sha2.js";
import { utf8ToBytes } from "@noble/hashes/utils.js";
//#region src/kernel/step-up.ts
/**
* The step-up contract of both planes (kernel apps, WS-1b, decision 4, R-003, R-042).
*
* A step-up request is a record of one privileged operation that waits for a human. The human
* answers it with a passkey assertion on the kernel origin (DP) or on the CP console. This module
* holds every shape and rule that two programs must agree on:
*
* 1. the request record, the server summary and the one view that the step-up page reads (R-003);
* 2. the operation digest, which binds a request to one org, method, path, query and body;
* 3. the WebAuthn challenge, which binds one assertion to one request and one server nonce;
* 4. the two 403 bodies `step_up_required` and `kernel_origin_required`, and the replay header;
* 5. the eight CP operations that owe a step-up (R-004, R-046, shared-libraries R-040,
*    cp-active-role D3) and the CP internal wire between release-engine and cp-auth-api
*    (`/internal/step-up/requests`, `/internal/step-up/redemptions`).
*
* core-api (WS-12a, WS-19) and cp-auth-api (WS-6b) serve the view. release-engine (WS-7a, WS-8a)
* calls the internal wire. WS-2b, WS-9, WS-15, WS-17 and WS-23 parse the view and the 403 bodies.
*
* Browser-safe: no Node builtin and no Node global. No schema uses `.transform()`, so each one stays
* representable in the JSON Schema that `createOpenApiDocument()` writes.
*/
/** The tier-2 window of decision 4: a step-up of the same kernel session family counts for 300 s. */
const STEP_UP_WINDOW_SECONDS = 300;
/** How long a step-up request waits in `pending`: 86,400 s, 24 hours (I-11, R-085). */
const STEP_UP_REQUEST_TTL_SECONDS = 86400;
/** How long an approval is good for its one use: 600 s, 10 minutes (I-11, R-085). */
const STEP_UP_APPROVAL_TTL_SECONDS = 600;
/**
* The header with which a caller repeats a call after a human approved its request. The value is
* the request id. The DP gateway strips only `x-zion-channel`, `x-zion-org-id` and
* `x-zion-version-subset`, so this header reaches core-api.
*/
const STEP_UP_HEADER = "x-zion-step-up";
/** The cap, in UTF-8 bytes, of the canonical text that {@link stepUpOperationDigest} hashes. */
const STEP_UP_CANONICAL_MAX_BYTES = 65536;
/** The longest path that {@link cpStepUpOperationOf} tests and that a record holds. */
const STEP_UP_PATH_MAX_LENGTH = 512;
/**
* The five states of a step-up request:
*
* - `pending`: it waits for a human, for at most {@link STEP_UP_REQUEST_TTL_SECONDS};
* - `approved`: a human approved it, and one use is open for {@link STEP_UP_APPROVAL_TTL_SECONDS};
* - `denied`: a human refused it;
* - `expired`: its wait or its approval ran out;
* - `consumed`: the approved operation ran once, and the request is spent (R-003, PRA-26).
*/
const STEP_UP_STATUSES = [
	"pending",
	"approved",
	"denied",
	"expired",
	"consumed"
];
/**
* The eight CP operations that owe a step-up, in kebab case (R-004, R-046). release-engine owns the
* routes of every kind except `operator-passkey-reset` and `role-elevation`. cp-auth-api owns those
* two and creates their requests in process. Kinds six and seven are the two library-right writes
* of 2026-10-12-shared-libraries (R-040 item 3, WS-22): a request for library rights from a
* consuming app or a consuming library, and the decision of a platform administrator on it. The
* last kind is the elevation of a CP session to a site role with more permissions
* (2026-10-22-cp-active-role, D3, D5, R-001): the passkey that `POST /v1/auth/session/active-role`
* asks for. Each new kind follows the earlier ones, so the position of each earlier kind stays as
* it was.
*/
const CP_STEP_UP_OPERATIONS = [
	"kernel-right-request",
	"kernel-right-decision",
	"release-statement-signature",
	"release-sources-change",
	"operator-passkey-reset",
	"library-right-request",
	"library-right-decision",
	"role-elevation"
];
/**
* The route of each CP operation: the method and an anchored path pattern over the raw path. The
* patterns admit no `.`, `%`, `?` or `#`, so a dot segment or an escape never matches.
*
* | operation | method | path |
* |---|---|---|
* | `kernel-right-request` | `POST` | `/v1/apps/:appId/kernel-rights/requests` |
* | `kernel-right-decision` | `POST` | `/v1/admin/kernel-rights/requests/:id/decision` |
* | `release-statement-signature` | `POST` | `/v1/releases/apps/:appId/release-statements/signature` |
* | `release-sources-change` | `PUT` | `/v1/apps/:appId/release-sources` |
* | `operator-passkey-reset` | `DELETE` | `/v1/operators/:id/passkeys` |
* | `library-right-request` | `POST` | `/v1/apps/:appId/library-rights/requests` |
* | `library-right-decision` | `POST` | `/v1/admin/library-rights/requests/:id/decision` |
* | `role-elevation` | `POST` | `/v1/auth/session/active-role` |
*/
const CP_STEP_UP_OPERATION_ROUTES = {
	"kernel-right-request": {
		method: "POST",
		pathPattern: /^\/v1\/apps\/[A-Za-z0-9-]{1,64}\/kernel-rights\/requests$/
	},
	"kernel-right-decision": {
		method: "POST",
		pathPattern: /^\/v1\/admin\/kernel-rights\/requests\/[0-9a-f-]{36}\/decision$/
	},
	"release-statement-signature": {
		method: "POST",
		pathPattern: /^\/v1\/releases\/apps\/[A-Za-z0-9-]{1,64}\/release-statements\/signature$/
	},
	"release-sources-change": {
		method: "PUT",
		pathPattern: /^\/v1\/apps\/[A-Za-z0-9-]{1,64}\/release-sources$/
	},
	"operator-passkey-reset": {
		method: "DELETE",
		pathPattern: /^\/v1\/operators\/[0-9a-f-]{36}\/passkeys$/
	},
	"library-right-request": {
		method: "POST",
		pathPattern: /^\/v1\/apps\/[A-Za-z0-9-]{1,64}\/library-rights\/requests$/
	},
	"library-right-decision": {
		method: "POST",
		pathPattern: /^\/v1\/admin\/library-rights\/requests\/[0-9a-f-]{36}\/decision$/
	},
	"role-elevation": {
		method: "POST",
		pathPattern: /^\/v1\/auth\/session\/active-role$/
	}
};
/**
* The CP operation whose method and path pattern match, or `null`. A path over
* {@link STEP_UP_PATH_MAX_LENGTH} characters answers `null` before any pattern runs. The method
* compare is exact, so `post` matches nothing. Never throws. Pure.
*/
function cpStepUpOperationOf(method, path) {
	if (path.length > 512) return null;
	for (const operation of CP_STEP_UP_OPERATIONS) {
		const route = CP_STEP_UP_OPERATION_ROUTES[operation];
		if (route.method === method && route.pathPattern.test(path)) return operation;
	}
	return null;
}
/**
* The digest that binds a step-up request to one operation of one org: `sha256DigestOf` of the
* UTF-8 bytes of the canonical JSON of `{ v: 1, orgId, method, path, query, body }`, with an
* `undefined` body read as `null`. Whitespace and key order give one digest. The guard (WS-19) and
* the CP gate (WS-7a) compute it when they create a request, and again when the caller repeats the
* call with {@link STEP_UP_HEADER}.
*
* It throws `KernelEncodingError('not_canonical')` in three cases, and the caller maps the throw to
* a refusal:
*
* 1. `path` does not start with `/`, or holds `?` or `#`;
* 2. a value holds a number that is not a safe integer, or another value that canonical JSON refuses;
* 3. the canonical text exceeds {@link STEP_UP_CANONICAL_MAX_BYTES} in UTF-8.
*
* Pure.
*/
function stepUpOperationDigest(input) {
	if (!input.path.startsWith("/") || input.path.includes("?") || input.path.includes("#")) throw new KernelEncodingError("not_canonical", "stepUpOperationDigest: the path must start with / and hold no ? or #");
	const text = kernelCanonicalJson({
		v: 1,
		orgId: input.orgId,
		method: input.method,
		path: input.path,
		query: input.query,
		body: input.body === void 0 ? null : input.body
	});
	const bytes = utf8ToBytes(text);
	if (bytes.length > 65536) throw new KernelEncodingError("not_canonical", `stepUpOperationDigest: the canonical text exceeds ${STEP_UP_CANONICAL_MAX_BYTES} bytes`);
	return sha256DigestOf(bytes);
}
/** The fewest bytes a step-up nonce holds. */
const STEP_UP_NONCE_MIN_BYTES = 32;
/**
* The WebAuthn challenge of one step-up request: the base64url (43 characters) of the SHA-256 of
* the canonical JSON of `{ nonce, operationDigest, requestId }`. One assertion so approves one
* request with one server nonce, and fails on any other request. `nonce` must be canonical
* base64url of at least 32 bytes; otherwise the function throws
* `KernelEncodingError('not_base64url')`. Pure: a view that derives the challenge writes nothing.
*/
function stepUpWebAuthnChallenge(input) {
	if (base64UrlDecode(input.nonce).length < STEP_UP_NONCE_MIN_BYTES) throw new KernelEncodingError("not_base64url", `stepUpWebAuthnChallenge: the nonce must hold at least ${STEP_UP_NONCE_MIN_BYTES} bytes`);
	const text = kernelCanonicalJson({
		nonce: input.nonce,
		operationDigest: input.operationDigest,
		requestId: input.requestId
	});
	return base64UrlEncode(sha256(utf8ToBytes(text)));
}
const lowerUuidSchema = z.string().length(36).regex(LOWER_UUID_PATTERN, "a lower-case UUID");
const stepUpPathSchema = z.string().max(512).regex(/^\/[\x21\x22\x24-\x3e\x40-\x7e]*$/, "a raw path with no ? or #");
/**
* The app slug grammar of the app manifest `appId` (`app-manifest.schema.json`): at most 30
* characters, a lower-case letter first, a letter or digit last, and kebab case between. The rights
* certificate claims (`rights-certificate.ts`) and the release statement (`release-statement.ts`)
* apply the same grammar. A requester record so names only a slug that a certificate can name. The
* test "S-15" pins the three parsers to one answer over each slug class.
*/
const APP_SLUG_PATTERN = /^[a-z][a-z0-9-]*[a-z0-9]$/;
const PERMISSION_PATTERN = /^[a-z][a-z0-9:._-]*$/;
const CLIENT_ID_PATTERN = /^[\x21-\x7e]+$/;
const stepUpSubjectSchema = z.discriminatedUnion("plane", [z.object({
	plane: z.literal("dp"),
	right: kernelRightSchema,
	tier: z.union([z.literal(1), z.literal(2)])
}).strict(), z.object({
	plane: z.literal("cp"),
	operation: z.enum(CP_STEP_UP_OPERATIONS)
}).strict()]);
const stepUpRequesterSchema = z.object({
	kind: z.enum([
		"kernel-session",
		"cp-session",
		"machine",
		"delegated"
	]),
	sub: lowerUuidSchema,
	appSlug: z.string().max(30).regex(APP_SLUG_PATTERN).nullable(),
	clientId: z.string().max(256).regex(CLIENT_ID_PATTERN).nullable()
}).strict().superRefine((requester, ctx) => {
	if ((requester.kind === "kernel-session" || requester.kind === "cp-session") && (requester.appSlug !== null || requester.clientId !== null)) ctx.addIssue({
		code: "custom",
		message: "a session requester names no app and no client"
	});
	if (requester.kind === "machine" && requester.clientId !== null) ctx.addIssue({
		code: "custom",
		path: ["clientId"],
		message: "a machine requester names no client"
	});
	if (requester.kind === "delegated" && requester.clientId === null) ctx.addIssue({
		code: "custom",
		path: ["clientId"],
		message: "a delegated requester names its client"
	});
});
/** The time fields that each status sets (`true`) or leaves `null` (`false`); `null` here means "either". */
const STATUS_TIME_RULES = {
	pending: {
		decidedAt: false,
		decidedBy: false,
		consumeBy: false,
		consumedAt: false
	},
	approved: {
		decidedAt: true,
		decidedBy: true,
		consumeBy: true,
		consumedAt: false
	},
	denied: {
		decidedAt: true,
		decidedBy: true,
		consumeBy: false,
		consumedAt: false
	},
	consumed: {
		decidedAt: true,
		decidedBy: true,
		consumeBy: true,
		consumedAt: true
	},
	expired: {
		decidedAt: null,
		decidedBy: null,
		consumeBy: null,
		consumedAt: false
	}
};
/**
* The strict parse of a {@link StepUpRequestRecord}. Beyond the field bounds it refuses three
* mixes: a CP subject with a requester that is not `cp-session` (and the reverse), a requester that
* breaks the rule of its kind, and a time field that its status does not allow.
*/
const stepUpRequestRecordSchema = z.object({
	id: lowerUuidSchema,
	status: z.enum(STEP_UP_STATUSES),
	subject: stepUpSubjectSchema,
	method: z.enum([
		"GET",
		"POST",
		"PUT",
		"PATCH",
		"DELETE"
	]),
	path: stepUpPathSchema,
	operationDigest: sha256DigestSchema,
	requester: stepUpRequesterSchema,
	requiredPermission: z.string().min(1).max(128).regex(PERMISSION_PATTERN).nullable(),
	createdAt: isoTimestampSchema,
	expiresAt: isoTimestampSchema,
	decidedAt: isoTimestampSchema.nullable(),
	decidedBy: lowerUuidSchema.nullable(),
	consumeBy: isoTimestampSchema.nullable(),
	consumedAt: isoTimestampSchema.nullable()
}).strict().superRefine((record, ctx) => {
	if (record.subject.plane === "cp" !== (record.requester.kind === "cp-session")) ctx.addIssue({
		code: "custom",
		path: ["requester", "kind"],
		message: "a CP subject goes with a cp-session requester, and only it"
	});
	const rules = STATUS_TIME_RULES[record.status];
	for (const field of [
		"decidedAt",
		"decidedBy",
		"consumeBy",
		"consumedAt"
	]) {
		const rule = rules[field];
		if (rule !== null && record[field] !== null !== rule) ctx.addIssue({
			code: "custom",
			path: [field],
			message: `status ${record.status} ${rule ? "sets" : "leaves null"} ${field}`
		});
	}
});
/** The longest summary title, in UTF-16 code units. */
const STEP_UP_SUMMARY_TITLE_MAX = 200;
/** The most lines a summary holds. */
const STEP_UP_SUMMARY_LINES_MAX = 20;
/** The longest line label, in UTF-16 code units. */
const STEP_UP_SUMMARY_LABEL_MAX = 64;
/** The longest line value, in UTF-16 code units. */
const STEP_UP_SUMMARY_VALUE_MAX = 500;
const stepUpSummaryLineSchema = z.object({
	label: displayTextSchema(1, 64),
	value: displayTextSchema(0, 500)
}).strict();
/**
* The strict parse of a {@link StepUpSummary}: a title of 1 to 200 code units and 1 to 20 lines,
* each label of 1 to 64 and each value of 0 to 500. It refuses a control, format (the bidi
* controls), surrogate or separator code point in any title, label or value, so a stored body cannot
* disguise the page.
*/
const stepUpSummarySchema = z.object({
	title: displayTextSchema(1, 200),
	lines: z.array(stepUpSummaryLineSchema).min(1).max(20)
}).strict();
const ELLIPSIS = "...";
/**
* A summary cell that the summary schema admits, from any text. Each refused code point (control,
* format, lone surrogate, line or paragraph separator) becomes the visible text `\u{<lower hex>}`.
* A result longer than `max` code units keeps its head up to a code point boundary and ends in
* `...`, at most `max` code units in all. It throws `TypeError` for a `max` that is not an integer
* from 4 to 500. Pure.
*/
function stepUpSummaryCell(text, max) {
	if (!Number.isInteger(max) || max < 4 || max > 500) throw new TypeError(`stepUpSummaryCell: max must be an integer from 4 to 500`);
	let shown = "";
	for (const ch of text) shown += REFUSED_DISPLAY_CODE_POINT.test(ch) ? `\\u{${ch.codePointAt(0).toString(16)}}` : ch;
	if (shown.length <= max) return shown;
	let end = max - 3;
	const last = shown.charCodeAt(end - 1);
	if (last >= 55296 && last <= 56319) end -= 1;
	return shown.slice(0, end) + ELLIPSIS;
}
/** The transports a credential may name (the WebAuthn list). */
const STEP_UP_TRANSPORTS = [
	"ble",
	"cable",
	"hybrid",
	"internal",
	"nfc",
	"smart-card",
	"usb"
];
const KNOWN_TRANSPORTS = new Set(STEP_UP_TRANSPORTS);
function isStepUpTransport(word) {
	return KNOWN_TRANSPORTS.has(word);
}
/**
* The known transports of a transport list, as WebAuthn requires: a relying party ignores an
* unknown value (WebAuthn level 2, section 5.2.1, R-096).
*
* Who calls it:
*
* - the register verify of cp-auth-api (WS-6a) and core-api (WS-10a), through
*   `webAuthnRegistrationForVerify` (`second-factor.ts`), before `verifyRegistrationResponse` and
*   before the store, so the stored list holds the known words only;
* - each producer of {@link StepUpPublicKeyOptions} and of the creation options, on the
*   `transports` of a stored passkey row. A row that core-api wrote before this contract can hold
*   any word, and {@link stepUpAllowCredentialSchema} admits only the seven known words.
*
* The result keeps each word of {@link STEP_UP_TRANSPORTS} at its first place in `words`, in the
* order of `words`. It drops an unknown word, a word in another case and a repeat. So it holds at
* most seven words, and every allowed-credential parse admits it. An empty input, or an input of
* unknown words alone, gives an empty array. The function never throws and does not change
* `words`. Pure.
*/
function stepUpTransportsOf(words) {
	const kept = [];
	for (const word of words) if (isStepUpTransport(word) && !kept.includes(word)) kept.push(word);
	return kept;
}
/** The strict parse of one allowed or excluded credential. Internal to the kernel wire modules. */
const stepUpAllowCredentialSchema = z.object({
	id: base64UrlTextSchema(1364),
	type: z.literal("public-key"),
	transports: z.array(z.enum(STEP_UP_TRANSPORTS)).max(STEP_UP_TRANSPORTS.length).optional()
}).strict();
/**
* The strict parse of {@link StepUpPublicKeyOptions}: a 43-character challenge, an `rpId` that is a
* lower-case host of 1 to 253 characters, a timeout of 1 to 600,000 ms, `userVerification:
* 'required'` alone, and 0 to 32 credentials.
*
* It admits the key `extensions` with the value `undefined` alone, the form that
* `generateAuthenticationOptions` returns when the producer passes no extensions. The parsed value
* then keeps that key, and JSON drops it. It refuses any other `extensions` value, an empty
* object among them. In JSON Schema the member renders as `{ "not": {} }`, so a JSON body that
* names `extensions` fails there too.
*/
const stepUpPublicKeyOptionsSchema = z.object({
	challenge: base64Url32BytesSchema,
	rpId: lowerHostSchema,
	timeout: z.int().min(1).max(6e5),
	userVerification: z.literal("required"),
	allowCredentials: z.array(stepUpAllowCredentialSchema).max(32),
	extensions: z.never().optional()
}).strict();
/**
* The strict parse of {@link StepUpRequestView}. It admits options only beside the status `pending`,
* and `null` beside every other status.
*/
const stepUpRequestViewSchema = z.object({
	request: stepUpRequestRecordSchema,
	summary: stepUpSummarySchema,
	publicKeyOptions: stepUpPublicKeyOptionsSchema.nullable()
}).strict().superRefine((view, ctx) => {
	if (view.publicKeyOptions !== null !== (view.request.status === "pending")) ctx.addIssue({
		code: "custom",
		path: ["publicKeyOptions"],
		message: "options go with a pending request, and only with it"
	});
});
/** The step-up page path of a request: `/step-up/<id>`. It throws `TypeError` for an id that is not a lower-case UUID. Pure. */
function stepUpPagePath(requestId) {
	if (!LOWER_UUID_PATTERN.test(requestId)) throw new TypeError("stepUpPagePath: the request id must be a lower-case UUID");
	return `/step-up/${requestId}`;
}
/**
* True when the `stepUpAt` claim lies within the tier-2 window: `0 <= nowSeconds - stepUpAt <=
* 300`. An absent claim, a claim in the future and a value that is not a finite number answer
* `false`. Pure.
*/
function isWithinStepUpWindow(stepUpAt, nowSeconds) {
	if (stepUpAt === void 0) return false;
	const age = nowSeconds - stepUpAt;
	return age >= 0 && age <= 300;
}
/**
* The strict parse of {@link StepUpRequiredBody}. `stepUpUrl` must be an https URL with no user,
* query or fragment, whose path is `/step-up/<stepUpRequestId>`.
*/
const stepUpRequiredBodySchema = z.object({
	code: z.literal("step_up_required"),
	action: z.literal("step_up"),
	stepUpRequestId: lowerUuidSchema,
	stepUpUrl: stepUpUrlSchema,
	expiresAt: isoTimestampSchema
}).strict().superRefine((body, ctx) => {
	if (!isStepUpUrlOf(body.stepUpUrl, body.stepUpRequestId)) ctx.addIssue({
		code: "custom",
		path: ["stepUpUrl"],
		message: "the URL names another request"
	});
});
/**
* The strict parse of {@link KernelOriginRequiredBody}. `kernelOrigin` is `https://` and a
* lower-case host with no port, path or trailing dot, at most 261 characters, or the empty string.
*/
const kernelOriginRequiredBodySchema = z.object({
	code: z.literal("kernel_origin_required"),
	action: z.literal("open_kernel_origin"),
	kernelOrigin: z.string().max(261).regex(/^(?:https:\/\/[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?(?:\.[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?)*)?$/, "https:// and a lower-case host, or the empty string")
}).strict();
/** The cp-auth-api route on which release-engine creates a CP step-up request. */
const CP_STEP_UP_REQUESTS_PATH = "/internal/step-up/requests";
/** The cp-auth-api route on which release-engine redeems an approved CP step-up request once. */
const CP_STEP_UP_REDEMPTIONS_PATH = "/internal/step-up/redemptions";
/** The most summary lines a create body holds. */
const CP_STEP_UP_CREATE_LINES_MAX = 12;
/** The longest summary value a create body holds, in UTF-16 code units. */
const CP_STEP_UP_CREATE_VALUE_MAX = 256;
/** The kinds that cp-auth-api creates in process. No service sends one on the internal wire. */
const CP_STEP_UP_IN_PROCESS_KINDS = /* @__PURE__ */ new Set(["operator-passkey-reset", "role-elevation"]);
/**
* The strict parse of {@link CpStepUpCreateInput}. It refuses:
*
* 1. the kinds `operator-passkey-reset` and `role-elevation`, which cp-auth-api creates in process
*    and no service sends;
* 2. a method and path that the route of `kind` in {@link CP_STEP_UP_OPERATION_ROUTES} does not match;
* 3. 0 or more than 12 summary lines, a label of 0 or more than 64 code units, a value of 0 or more
*    than 256, and a refused code point in a label or a value.
*/
const cpStepUpCreateSchema = z.object({
	operatorId: lowerUuidSchema,
	kind: z.enum(CP_STEP_UP_OPERATIONS),
	method: z.enum([
		"POST",
		"PUT",
		"DELETE"
	]),
	path: z.string().max(512),
	operationDigest: sha256DigestSchema,
	summary: z.array(z.object({
		label: displayTextSchema(1, 64),
		value: displayTextSchema(1, 256)
	}).strict()).min(1).max(12)
}).strict().superRefine((input, ctx) => {
	if (CP_STEP_UP_IN_PROCESS_KINDS.has(input.kind)) {
		ctx.addIssue({
			code: "custom",
			path: ["kind"],
			message: "cp-auth-api creates this kind in process"
		});
		return;
	}
	const route = CP_STEP_UP_OPERATION_ROUTES[input.kind];
	if (route.method !== input.method || !route.pathPattern.test(input.path)) ctx.addIssue({
		code: "custom",
		path: ["path"],
		message: `the method and path are not the route of ${input.kind}`
	});
});
/** The strict parse of {@link CpStepUpCreated}. `approveUrl` must be the https step-up page of `id`. */
const cpStepUpCreatedSchema = z.object({
	id: lowerUuidSchema,
	expiresAt: isoTimestampSchema,
	approveUrl: stepUpUrlSchema
}).strict().superRefine((created, ctx) => {
	if (!isStepUpUrlOf(created.approveUrl, created.id)) ctx.addIssue({
		code: "custom",
		path: ["approveUrl"],
		message: "the URL names another request"
	});
});
/** The strict parse of {@link CpStepUpRedeemInput}. */
const cpStepUpRedeemSchema = z.object({
	id: lowerUuidSchema,
	operatorId: lowerUuidSchema,
	operationDigest: sha256DigestSchema
}).strict();
/**
* Why a redeem changed no row:
*
* - `not_found`: no request, or a request of another service;
* - `not_approved`: the request still waits;
* - `denied`: a human refused it;
* - `expired`: its wait or its approval ran out;
* - `consumed`: an earlier redeem spent it;
* - `mismatch`: another operator or another operation digest.
*/
const CP_STEP_UP_REDEEM_REFUSALS = [
	"not_found",
	"not_approved",
	"denied",
	"expired",
	"consumed",
	"mismatch"
];
/** The strict parse of {@link CpStepUpRedeemResult}. */
const cpStepUpRedeemResultSchema = z.discriminatedUnion("outcome", [z.object({
	outcome: z.literal("redeemed"),
	approvedAt: isoTimestampSchema,
	credentialId: base64UrlTextSchema(1364)
}).strict(), z.object({
	outcome: z.literal("refused"),
	reason: z.enum(CP_STEP_UP_REDEEM_REFUSALS)
}).strict()]);
//#endregion
export { CP_STEP_UP_CREATE_LINES_MAX, CP_STEP_UP_CREATE_VALUE_MAX, CP_STEP_UP_OPERATIONS, CP_STEP_UP_OPERATION_ROUTES, CP_STEP_UP_REDEEM_REFUSALS, CP_STEP_UP_REDEMPTIONS_PATH, CP_STEP_UP_REQUESTS_PATH, STEP_UP_APPROVAL_TTL_SECONDS, STEP_UP_CANONICAL_MAX_BYTES, STEP_UP_HEADER, STEP_UP_PATH_MAX_LENGTH, STEP_UP_REQUEST_TTL_SECONDS, STEP_UP_STATUSES, STEP_UP_SUMMARY_LABEL_MAX, STEP_UP_SUMMARY_LINES_MAX, STEP_UP_SUMMARY_TITLE_MAX, STEP_UP_SUMMARY_VALUE_MAX, STEP_UP_TRANSPORTS, STEP_UP_WINDOW_SECONDS, cpStepUpCreateSchema, cpStepUpCreatedSchema, cpStepUpOperationOf, cpStepUpRedeemResultSchema, cpStepUpRedeemSchema, isWithinStepUpWindow, kernelOriginRequiredBodySchema, stepUpAllowCredentialSchema, stepUpOperationDigest, stepUpPagePath, stepUpPublicKeyOptionsSchema, stepUpRequestRecordSchema, stepUpRequestViewSchema, stepUpRequiredBodySchema, stepUpSummaryCell, stepUpSummarySchema, stepUpTransportsOf, stepUpWebAuthnChallenge };

//# sourceMappingURL=step-up.js.map